import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  getOperationCodeModules,
  getOperationCodes,
  updateOperationCode,
  type OperationCodeQuery,
} from '../api/operationCodeApi'
import type { OperationCodeUpdate } from '../types'

const KEY = 'admin-operation-codes'

/**
 * İşlem Kodu sunucu-durumu (045): filtreli/sayfalı liste + modül seçenekleri + ad güncelleme.
 *
 * <p><b>{@code i18n.language} queryKey'in parçasıdır:</b> hem liste satırlarındaki {@code moduleName}
 * hem de modül seçeneklerinin adları sunucuda {@code Accept-Language}'e göre çözülür. Dil TR↔EN
 * değiştiğinde anahtar değişmezse önbellekten eski dildeki yanıt sunulur ve Modül sütunu ile filtre
 * etiketleri eski dilde kalır.
 *
 * <p><b>{@code keepPreviousData}:</b> sunucu-taraflı sayfalamada sayfa değişirken {@code isLoading}
 * yeniden {@code true} olur; paylaşılan {@code DataTable}'ın iskelet dalı paginator'ı render etmediği
 * için sayfalama kontrolü kaybolup tablo titrer. Önceki veri korunarak bu önlenir (repodaki diğer
 * sunucu-sayfalı listelerle aynı kalıp).
 */
export function useOperationCodes(query: OperationCodeQuery) {
  const qc = useQueryClient()
  const { i18n } = useTranslation()

  const list = useQuery({
    queryKey: [KEY, i18n.language, query.q ?? '', query.moduleId ?? null, query.page, query.size],
    queryFn: () => getOperationCodes(query),
    placeholderData: keepPreviousData,
  })

  const modules = useQuery({
    queryKey: [KEY, 'modules', i18n.language],
    queryFn: getOperationCodeModules,
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: OperationCodeUpdate }) =>
      updateOperationCode(id, body),
    // Kaydetme sonrası listeyi tazele → ad sütunları güncel (SC-002).
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  })

  return { list, modules, update }
}
